import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Archive, Unarchive } from "@mui/icons-material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { Alert, Button, Snackbar, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { Stack } from "@mui/system";

import { SearchField } from "@/components/SearchField";
import { FullPageSpinner } from "@/components/Spinner";
import { Column } from "@/components/sort/SelectOrderByComponent";
import TableSort from "@/components/sort/TableSort";
import {
  clearSelection,
  selectActiveTab,
  selectDomain,
  selectSelectedAlertIds,
  selectSelectedDomainId,
  setActiveTab,
  setSelectedAlerts,
} from "@/pages/browser-defender/demo/passwordMisuseUpdated/service/passwordMisuseSlice";
import {
  AggregatedAlertType,
  BrowsingAction,
  PasswordMisuseSourceParams,
  PasswordMisuseTab,
  RemediationType,
} from "@/pages/browser-defender/demo/passwordMisuseUpdated/service/types";
import { ThreatPagination } from "@/pages/threats/components/PaginatedTable";
import {
  ThreatsDetailsPane,
  ThreatsLayout,
  ThreatsListPane,
  ThreatsPaginationPane,
  ThreatsTitlePane,
  ThreatsToolPane,
} from "@/pages/threats/components/ThreatsLayout";
import { appDispatch, appSelector } from "@/store";
import { useUserContext } from "@/utils/contexts/UserContext";
import { isDemoUser } from "@/utils/contexts/demo";
import { usePaginationData } from "@/utils/pagination";

import {
  AllowBrowsingDialog,
  BlockBrowsingDialog,
} from "./components/PasswordMisuseActionDialogs";
import PasswordMisuseDomainDetails from "./components/PasswordMisuseDomainDetails";
import PasswordMisuseDomainRow from "./components/PasswordMisuseDomainRow";
import { PasswordMisuseFilterTools } from "./components/PasswordMisuseFilterTools";
import PasswordMisuseTabs from "./components/PasswordMisuseTabs";
import {
  passwordMisuseApi,
  useAllowDomainMutation,
  useArchiveAlertsMutation,
  useBlockDomainMutation,
  useGetArchivedAlertsQuery,
  useGetPhishingAlertsQuery,
  useGetReuseAlertsQuery,
  useReuseDomainMutation,
  useUnarchiveAlertsMutation,
} from "./service/passwordMisuseApi";

type FilterValues = {
  type: AggregatedAlertType | null;
  remediation: RemediationType | null;
};

type SortConfig = {
  column: string;
  direction: number;
}[];

const PasswordMisuseUpdated = () => {
  const activeTab = appSelector(selectActiveTab);
  const { t } = useTranslation();
  const dispatch = appDispatch();

  const sortingColumns: Column[] = [
    { label: t("Name"), name: "name" },
    { label: t("Alert Count"), name: "alertCount" },
    { label: t("User Count"), name: "userCount" },
    { label: t("Latest Event"), name: "latestEvent" },
    { label: t("Type"), name: "type" },
    { label: t("Remediation"), name: "remediation" },
  ];

  const { data: phishingData, isLoading: isLoadingPhishing } =
    useGetPhishingAlertsQuery(undefined, {});
  const { data: reuseData, isLoading: isLoadingReuse } = useGetReuseAlertsQuery(
    undefined,
    {},
  );
  const { data: archivedData, isLoading: isLoadingArchived } =
    useGetArchivedAlertsQuery(undefined, {});

  const [allowDomain] = useAllowDomainMutation();
  const [reuseDomain] = useReuseDomainMutation();
  const [blockDomain] = useBlockDomainMutation();
  const [archiveAlerts] = useArchiveAlertsMutation();
  const [unarchiveAlerts] = useUnarchiveAlertsMutation();

  // Get current data based on active tab
  const domains = useMemo(() => {
    switch (activeTab) {
      case "phishing":
        return phishingData || [];
      case "reuse":
        return reuseData || [];
      case "archived":
        return archivedData || [];
      default:
        return [];
    }
  }, [activeTab, phishingData, reuseData, archivedData]);
  const selectedDomainId = appSelector(selectSelectedDomainId);
  const isLoading = isLoadingPhishing || isLoadingReuse || isLoadingArchived;
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<FilterValues>({
    type: null,
    remediation: null,
  });
  const [sorting, setSorting] = useState<SortConfig>([
    { column: "latestEvent", direction: -1 },
  ]);
  const selectedAlertIds = appSelector(selectSelectedAlertIds);

  const paginationData = usePaginationData();

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const [allowDialogOpen, setAllowDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);

  // Derive filtered domains using useMemo
  const filteredDomains = useMemo(() => {
    if (domains.length === 0) return [];

    // Filter by search term
    let result = searchTerm
      ? domains.filter((domain) =>
          domain.name.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : [...domains];

    // Apply type and remediation filters
    if (filters.type) {
      result = result.filter((domain) => domain.type === filters.type);
    }

    if (filters.remediation) {
      result = result.filter(
        (domain) => domain.remediation === filters.remediation,
      );
    }

    // Apply sorting
    if (sorting.length > 0) {
      result = [...result].sort((a, b) => {
        for (const sort of sorting) {
          if (a[sort.column] < b[sort.column]) return -sort.direction;
          if (a[sort.column] > b[sort.column]) return sort.direction;
        }
        return 0;
      });
    }

    return result;
  }, [domains, searchTerm, filters, sorting]);

  const paginatedDomains = useMemo(() => {
    const start = paginationData.startIndex;
    const end = start + paginationData.rowsPerPage;
    return filteredDomains.slice(start, end);
  }, [filteredDomains, paginationData.startIndex, paginationData.rowsPerPage]);

  const selectedDomainAlert = useMemo(() => {
    if (selectedDomainId === null || selectedDomainId === undefined)
      return null;
    return domains.find((domain) => domain.id === selectedDomainId) || null;
  }, [domains, selectedDomainId]);

  const handleTabChange = (params: PasswordMisuseSourceParams) => {
    dispatch(setActiveTab(params.tab as PasswordMisuseTab));
    setSearchTerm("");
    setFilters({ type: null, remediation: null });
    paginationData.changePage(0);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    paginationData.changePage(0);
  };

  const handleFilterChange = (
    newFilters: React.SetStateAction<FilterValues>,
  ) => {
    setFilters(newFilters);
    paginationData.changePage(0);
  };

  const handleSortChange = (newSorts: SortConfig) => {
    setSorting(newSorts);
    paginationData.changePage(0);
  };

  const handleDomainSelect = (domainId: number) => {
    dispatch(selectDomain(domainId));
  };

  const handleAlertSelect = (
    alertId: number,
    selected: boolean,
    alertIds?: number[],
  ) => {
    if (alertIds) {
      dispatch(setSelectedAlerts(alertIds));
    } else {
      if (selected) {
        dispatch(setSelectedAlerts([...selectedAlertIds, alertId]));
      } else {
        dispatch(
          setSelectedAlerts(selectedAlertIds.filter((id) => id !== alertId)),
        );
      }
    }
  };

  const handleBrowsingAction = async (action: BrowsingAction) => {
    const selectedDomain = domains.find((d) => d.id === selectedDomainId);
    if (!selectedDomain) return;

    const phishingAlertIds =
      selectedDomainAlert?.passwordMisuseAlerts
        ?.filter(
          (alert) =>
            selectedAlertIds.includes(alert.id) && alert.type === "phishing",
        )
        .map((alert) => alert.id) || [];

    const reuseAlertIds =
      selectedDomainAlert?.passwordMisuseAlerts
        ?.filter(
          (alert) =>
            selectedAlertIds.includes(alert.id) && alert.type === "reuse",
        )
        .map((alert) => alert.id) || [];

    const payload = {
      domain: selectedDomain.name,
      phishingAlertIds,
      reuseAlertIds,
      reason: action.reason,
    };

    try {
      // Make the actual API call without any cache manipulation
      if (action.action === "allow") {
        await allowDomain(payload).unwrap();
      } else if (action.action === "reuse") {
        await reuseDomain(payload).unwrap();
      } else if (action.action === "block") {
        await blockDomain(payload).unwrap();
      } else if (action.action === "archive") {
        await archiveAlerts(payload).unwrap();
      } else if (action.action === "unarchive") {
        await unarchiveAlerts(payload).unwrap();
      }

      // Invalidate the cache to trigger a refetch of all data
      dispatch(passwordMisuseApi.util.invalidateTags(["PasswordMisuse"]));

      // Show success notification
      setNotification({
        open: true,
        message: getSuccessMessage(action.action),
        severity: "success",
      });

      dispatch(clearSelection());
    } catch (error) {
      dispatch(passwordMisuseApi.util.invalidateTags(["PasswordMisuse"]));

      setNotification({
        open: true,
        message: `Error: ${error.message || "An unknown error occurred"}`,
        severity: "error",
      });
    }
  };

  const getSuccessMessage = (action: string): string => {
    switch (action) {
      case "allow":
        return "Domain marked as safe successfully";
      case "reuse":
        return "Domain marked for password reuse monitoring successfully";
      case "block":
        return "Domain blocked successfully";
      case "archive":
        return "Alerts archived successfully";
      case "unarchive":
        return "Alerts unarchived successfully";
      default:
        return "Action completed successfully";
    }
  };
  const handleExport = () => {
    const csvData = filteredDomains.map((domain) => {
      return {
        domain: domain.name,
        type: domain.type,
        alertCount: domain.alertCount,
        userCount: domain.userCount,
        remediation: domain.remediation,
        latestEvent: domain.latestEvent,
      };
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Domain,Type,Alert count,User count,Remediation,Report date\n" +
      csvData
        .map(
          (row) =>
            `${row.domain},${row.type},${row.alertCount},${row.userCount},${row.remediation},${row.latestEvent}`,
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "password_misuse_domains.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setNotification({
      open: true,
      message: t("CSV downloaded successfully"),
      severity: "success",
    });
  };

  const tableHeaders = (
    <div
      style={{
        display: "flex",
        width: "100%",
        padding: "8px 16px",
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
        fontWeight: "bold",
      }}
    >
      <div style={{ flex: 2 }}>
        <Typography>{t("Domain")}</Typography>
      </div>
      <div style={{ flex: 0.5, textAlign: "center" }}>
        <Typography>{t("Alerts")}</Typography>
      </div>
      <div style={{ flex: 0.5, textAlign: "center" }}>
        <Typography>{t("Users")}</Typography>
      </div>
      <div style={{ flex: 0.7, textAlign: "center" }}>
        <Typography>{t("Type")}</Typography>
      </div>
      <div style={{ flex: 0.7, textAlign: "center" }}>
        <Typography>{t("Remediation")}</Typography>
      </div>
    </div>
  );

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const info_user = useUserContext();
  if (!isDemoUser(info_user.email)) {
    return <Typography>{t("This feature will come in the future")}</Typography>;
  }

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: 0,
        height: "100%",
      }}
    >
      <ThreatsLayout>
        <ThreatsTitlePane>
          <PasswordMisuseTabs onChange={handleTabChange} />
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, my: 1 }}
          >
            {activeTab === "archived" ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Unarchive />}
                  onClick={() => {
                    handleBrowsingAction({
                      action: "unarchive",
                      domain: selectedDomainAlert?.name || "",
                      alertIds: selectedAlertIds,
                    });
                  }}
                  disabled={selectedDomainId === null}
                  sx={{ height: "50px", width: "130px" }}
                >
                  {t("Unarchive alerts")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => {
                    setAllowDialogOpen(true);
                  }}
                  disabled={selectedDomainId === null}
                  sx={{ height: "50px", width: "130px" }}
                >
                  {t("Allow browsing")}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => {
                    setBlockDialogOpen(true);
                  }}
                  disabled={selectedDomainId === null}
                  sx={{ height: "50px", width: "130px" }}
                >
                  {t("Block browsing")}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Archive />}
                  onClick={() => {
                    handleBrowsingAction({
                      action: "archive",
                      domain: selectedDomainAlert?.name || "",
                      alertIds: selectedAlertIds,
                    });
                  }}
                  disabled={selectedDomainId === null}
                  sx={{ height: "50px", width: "130px" }}
                >
                  {t("Archive alerts")}
                </Button>
              </>
            )}
          </Box>
        </ThreatsTitlePane>

        <ThreatsToolPane>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchField value={searchTerm} onChange={handleSearch} />
            <PasswordMisuseFilterTools
              onFilterChange={handleFilterChange}
              currentTab={activeTab}
            />
            <TableSort
              setOrderBy={handleSortChange}
              columns={sortingColumns}
              defaultOrder={[{ column: "latestEvent", direction: -1 }]}
            />
            <Button
              variant="outlined"
              onClick={handleExport}
              startIcon={<DownloadIcon />}
              sx={{ height: "33px" }}
            >
              {t("Export")}
            </Button>
          </Stack>
        </ThreatsToolPane>

        <ThreatsListPane>
          {tableHeaders}
          <div style={{ overflowY: "scroll", height: "calc(100% - 40px)" }}>
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <FullPageSpinner />
              </div>
            ) : paginatedDomains.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography variant="h5">
                  {searchTerm
                    ? t("No domains match your search")
                    : t("No domains to display")}
                </Typography>
              </div>
            ) : (
              paginatedDomains.map((domain) => (
                <PasswordMisuseDomainRow
                  key={domain.id}
                  domain={domain}
                  onSelect={handleDomainSelect}
                  isSelected={selectedDomainId === domain.id}
                />
              ))
            )}
          </div>
        </ThreatsListPane>

        <ThreatsPaginationPane>
          <ThreatPagination
            pagination={{
              startIndex: paginationData.startIndex,
              currentPage: paginationData.currentPage,
              rowsPerPage: paginationData.rowsPerPage,
              changePage: paginationData.changePage,
              changePageSize: paginationData.changePageSize,
            }}
            totalCount={filteredDomains.length}
          />
        </ThreatsPaginationPane>

        <ThreatsDetailsPane>
          <PasswordMisuseDomainDetails
            isLoading={isLoading}
            domain={selectedDomainAlert}
            onAlertSelect={handleAlertSelect}
            selectedAlertIds={selectedAlertIds}
          />
        </ThreatsDetailsPane>
      </ThreatsLayout>
      <AllowBrowsingDialog
        open={allowDialogOpen}
        domainName={selectedDomainAlert?.name || ""}
        onClose={() => setAllowDialogOpen(false)}
        onConfirm={handleBrowsingAction}
        selectedAlertIds={selectedAlertIds}
        selectedUrls={
          selectedDomainAlert && selectedDomainAlert.passwordMisuseAlerts
            ? Array.from(
                new Set(
                  selectedDomainAlert.passwordMisuseAlerts
                    .filter((alert) => selectedAlertIds.includes(alert.id))
                    .map((alert) => alert.url),
                ),
              )
            : []
        }
      />
      <BlockBrowsingDialog
        open={blockDialogOpen}
        domainName={selectedDomainAlert?.name || ""}
        onClose={() => setBlockDialogOpen(false)}
        onConfirm={handleBrowsingAction}
        selectedAlertIds={selectedAlertIds}
        selectedUrls={
          selectedDomainAlert && selectedDomainAlert.passwordMisuseAlerts
            ? Array.from(
                new Set(
                  selectedDomainAlert.passwordMisuseAlerts
                    .filter((alert) => selectedAlertIds.includes(alert.id))
                    .map((alert) => alert.url),
                ),
              )
            : []
        }
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PasswordMisuseUpdated;
